import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  ArrowRight, User, Briefcase, Building2, Leaf, Cpu, Rocket,
  BadgeCheck, Sparkles, ExternalLink, Handshake, TrendingUp, ShieldCheck, LogIn,
} from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import FloatingActions from "../components/FloatingActions"; // eslint-disable-line no-unused-vars
import SEO from "../components/SEO";
import CandidaturaPartnerModal from "../components/CandidaturaPartnerModal";
import { Button } from "../components/ui/button";
import { CONTACT } from "../data/mock";

const API = process.env.REACT_APP_BACKEND_URL;

const HERO_IMG =
  "https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80";

const PERCORSI = [
  {
    id: "privato",
    icon: User,
    titolo: "Sei un privato cittadino",
    payoff: "Trasforma le tue competenze digitali in un reddito extra reale.",
    img: "https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=1200&q=80",
    bullets: [
      "Percorsi guidati per monetizzare passioni e conoscenze digitali (creator, tutor, micro-servizi).",
      "Formazione base su AI, produttività e strumenti no-code per generare valore da casa.",
      "Accesso a bandi, incentivi e opportunità locali grazie alle nostre convenzioni.",
    ],
    kpi: [
      { label: "Ore di formazione", value: "40h+" },
      { label: "Progetti reali", value: "12" },
      { label: "Partner attivi", value: "20+" },
    ],
    cta: "Scopri il percorso Privato",
  },
  {
    id: "professionista",
    icon: Briefcase,
    titolo: "Sei un professionista",
    payoff: "Aggiungi una linea di business digitale e sostenibile alla tua attività.",
    img: "https://images.unsplash.com/photo-1551836022-d5d88e9218df?auto=format&fit=crop&w=1200&q=80",
    bullets: [
      "Specializzazioni verticali su AI, blockchain, ESG e consulenza tecnologica applicata.",
      "Iscrizione al Registro Nazionale ACDT con timbro ufficiale e visibilità pubblica.",
      "Convenzioni con aziende partner per progetti in outsourcing e prime commesse.",
    ],
    kpi: [
      { label: "Registro Pubblico", value: "SI" },
      { label: "Convenzioni B2B", value: "15+" },
      { label: "Ticket medio", value: "€ 1.2k" },
    ],
    cta: "Scopri il percorso Professionista",
  },
  {
    id: "azienda",
    icon: Building2,
    titolo: "Sei un'azienda",
    payoff: "Accelera la trasformazione digitale e sostenibile con la nostra rete.",
    img: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
    bullets: [
      "Audit iniziale gratuito su digitalizzazione, sostenibilità e adeguamento normativo.",
      "Formazione on-site per il team con rilascio di attestati validi per il registro CFDT.",
      "Accesso a incentivi 4.0/5.0, credito d'imposta e agevolazioni ESG con supporto operativo.",
    ],
    kpi: [
      { label: "Audit gratuito", value: "1h" },
      { label: "Team formati", value: "35+" },
      { label: "Risparmio medio", value: "18%" },
    ],
    cta: "Scopri il percorso Aziende",
  },
];

const PILLARS = [
  {
    icon: Cpu,
    titolo: "Digitale",
    text: "Strumenti concreti: AI generativa, automazione, no-code, e-commerce, analytics.",
  },
  {
    icon: Rocket,
    titolo: "Tecnologia",
    text: "Blockchain, IoT, cybersecurity, cloud: le competenze abilitanti del prossimo decennio.",
  },
  {
    icon: Leaf,
    titolo: "Sostenibilità",
    text: "ESG, economia circolare, adeguamento normativo e rendicontazione di sostenibilità.",
  },
];

export default function OpportunitaExtra() {
  const [partners, setPartners] = useState([]);
  const [loadingPartners, setLoadingPartners] = useState(true);
  const [candidaturaOpen, setCandidaturaOpen] = useState(false);

  useEffect(() => {
    document.title = "Opportunità Extra – ACDT | Digitale, Tecnologia, Sostenibilità";
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/api/partners`)
      .then((r) => setPartners(r.data || []))
      .catch(() => setPartners([]))
      .finally(() => setLoadingPartners(false));
  }, []);

  return (
    <div className="relative overflow-x-hidden bg-slate-50">
      <SEO
        title="Opportunità Extra ACDT – Digitale, Tecnologia, Sostenibilità"
        description="Grazie all'Associazione ACDT puoi accedere a percorsi dedicati per privati, professionisti e aziende: guadagna un reddito extra sfruttando digitale, tecnologia e sostenibilità con il supporto della nostra rete di partner."
      />
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[520px] flex items-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={HERO_IMG}
            alt="Opportunità digitali ACDT"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0a1f55]/95 via-[#102a6b]/85 to-[#1e3a8a]/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 py-20 md:py-28 text-white">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#fbbf24] mb-4">
            Percorsi Extra ACDT
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] max-w-4xl">
            Un reddito in più grazie a{" "}
            <span className="text-[#fbbf24]">digitale, tecnologia</span> e{" "}
            <span className="text-[#fbbf24]">sostenibilità</span>.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-200/90 max-w-3xl leading-relaxed">
            Grazie all'Associazione ACDT e alla nostra rete di aziende partner attiviamo
            percorsi dedicati per <strong>privati, professionisti e aziende</strong>:
            competenze, strumenti, opportunità di lavoro e connessioni concrete.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#percorsi">
              <Button
                data-testid="opportunita-cta-scopri"
                className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1f55] font-bold h-12 px-6"
              >
                Scopri i percorsi
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </a>
            <Link to="/area-soci">
              <Button
                data-testid="opportunita-cta-socio"
                variant="outline"
                className="h-12 bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
              >
                <LogIn className="mr-2 h-4 w-4" />
                Accesso Soci
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
              I tre pilastri
            </div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">
              Tre leve, un'unica rete: la tua opportunità concreta.
            </h2>
            <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
              Uniamo formazione certificata, strumenti operativi e una rete di aziende
              partner selezionate per generare valore reale, misurabile e sostenibile.
            </p>
          </div>
          <div className="mt-10 grid md:grid-cols-3 gap-6">
            {PILLARS.map((p) => {
              const I = p.icon;
              return (
                <div
                  key={p.titolo}
                  data-testid={`pillar-${p.titolo.toLowerCase()}`}
                  className="p-7 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#f59e0b]/40 hover:shadow-lg transition-all"
                >
                  <div className="h-12 w-12 rounded-xl bg-[#0a1f55] text-[#fbbf24] flex items-center justify-center mb-4">
                    <I className="h-6 w-6" />
                  </div>
                  <h3 className="text-xl font-extrabold text-[#0a1f55]">{p.titolo}</h3>
                  <p className="mt-2 text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Percorsi */}
      <section id="percorsi" className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
              I percorsi dedicati
            </div>
            <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">
              Scegli il percorso più adatto a te.
            </h2>
            <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed">
              Ogni percorso è progettato con obiettivi specifici, contenuti verticali e
              partnership su misura per il tuo profilo.
            </p>
          </div>

          <div className="mt-12 space-y-16">
            {PERCORSI.map((p, idx) => {
              const I = p.icon;
              const reverse = idx % 2 === 1;
              return (
                <div
                  key={p.id}
                  id={p.id}
                  data-testid={`percorso-${p.id}`}
                  className={`grid lg:grid-cols-12 gap-10 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
                >
                  <div className="lg:col-span-6">
                    <div className="relative rounded-3xl overflow-hidden shadow-xl group">
                      <img
                        src={p.img}
                        alt={p.titolo}
                        className="w-full h-[360px] md:h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f55]/70 via-transparent to-transparent" />
                      <div className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
                        <div className="h-11 w-11 rounded-xl bg-[#f59e0b] text-[#0a1f55] flex items-center justify-center shadow-lg">
                          <I className="h-5 w-5" />
                        </div>
                        <div className="text-white font-extrabold text-lg">{p.titolo}</div>
                      </div>
                    </div>
                  </div>

                  <div className="lg:col-span-6">
                    <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
                      Percorso {p.id}
                    </div>
                    <h3 className="mt-2 text-2xl md:text-3xl font-extrabold text-[#0a1f55]">
                      {p.payoff}
                    </h3>
                    <ul className="mt-5 space-y-3">
                      {p.bullets.map((b, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-700 leading-relaxed">
                          <BadgeCheck className="h-5 w-5 text-[#f59e0b] flex-shrink-0 mt-0.5" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-6 grid grid-cols-3 gap-3">
                      {p.kpi.map((k, i) => (
                        <div
                          key={i}
                          className="rounded-xl bg-white border border-slate-200 p-3 text-center"
                        >
                          <div className="text-xl font-extrabold text-[#0a1f55]">{k.value}</div>
                          <div className="text-[11px] uppercase tracking-wider text-slate-500 mt-0.5">
                            {k.label}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-6 flex flex-wrap gap-3">
                      <Link to="/area-soci">
                        <Button
                          data-testid={`cta-percorso-${p.id}`}
                          className="bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-bold h-11 px-5"
                        >
                          <LogIn className="mr-2 h-4 w-4" />
                          Accesso Soci
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section id="partners" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="flex items-center gap-3 mb-2">
            <Handshake className="h-5 w-5 text-[#f59e0b]" />
            <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
              Grazie ai nostri partner
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f55] max-w-3xl">
            Una rete di aziende selezionate al tuo fianco.
          </h2>
          <p className="mt-4 text-slate-600 text-base md:text-lg leading-relaxed max-w-3xl">
            Le opportunità che offriamo sono possibili grazie a un ecosistema di aziende
            partner che condivide con noi la missione di crescita digitale e sostenibile.
          </p>

          <div className="mt-10">
            {loadingPartners ? (
              <div data-testid="partners-loading" className="text-slate-500 text-sm">
                Caricamento partner…
              </div>
            ) : partners.length === 0 ? (
              <div
                data-testid="partners-empty"
                className="p-10 rounded-2xl border-2 border-dashed border-slate-200 text-center"
              >
                <Sparkles className="h-8 w-8 text-[#f59e0b] mx-auto mb-3" />
                <div className="text-slate-700 font-semibold">
                  Nuovi partner in arrivo. Vuoi entrare nella rete?
                </div>
                <a
                  href={`mailto:${CONTACT.email}?subject=Partnership%20ACDT`}
                  className="mt-3 inline-block text-[#0a1f55] font-bold underline"
                >
                  Contattaci per una partnership
                </a>
              </div>
            ) : (
              <div
                data-testid="partners-grid"
                className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5"
              >
                {partners.map((pt) => (
                  <a
                    key={pt.id}
                    href={pt.sito_url || "#"}
                    target={pt.sito_url ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`partner-card-${pt.id}`}
                    className="group flex flex-col items-center justify-between p-5 rounded-2xl bg-slate-50 border border-slate-200 hover:border-[#f59e0b]/50 hover:bg-white hover:shadow-lg transition-all"
                  >
                    <div className="h-20 w-full flex items-center justify-center">
                      {pt.logo_url ? (
                        <img
                          src={pt.logo_url.startsWith("http") ? pt.logo_url : `${API}${pt.logo_url}`}
                          alt={pt.nome}
                          className="max-h-16 max-w-full object-contain"
                        />
                      ) : (
                        <div className="h-16 w-16 rounded-xl bg-[#0a1f55]/5 flex items-center justify-center text-[#0a1f55] font-extrabold text-2xl">
                          {pt.nome.charAt(0)}
                        </div>
                      )}
                    </div>
                    <div className="mt-3 text-center">
                      <div className="font-bold text-[#0a1f55] text-sm leading-tight">
                        {pt.nome}
                      </div>
                      {pt.descrizione && (
                        <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">
                          {pt.descrizione}
                        </div>
                      )}
                      {pt.sito_url && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[#f59e0b] opacity-0 group-hover:opacity-100 transition-opacity">
                          Visita <ExternalLink className="h-3 w-3" />
                        </div>
                      )}
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="mt-10 p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#f59e0b]/10 to-[#fbbf24]/5 border border-[#f59e0b]/20 flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-8 w-8 text-[#f59e0b] flex-shrink-0" />
              <div>
                <div className="font-extrabold text-[#0a1f55]">
                  Sei un'azienda? Diventa partner ACDT.
                </div>
                <div className="text-sm text-slate-600">
                  Entra nella nostra rete e accedi a talenti, progetti e progetti co-finanziati.
                </div>
              </div>
            </div>
            <Button
              data-testid="opportunita-cta-partnership"
              onClick={() => setCandidaturaOpen(true)}
              className="bg-[#0a1f55] hover:bg-[#102a6b] text-white font-bold h-11 px-5"
            >
              Proponi una partnership
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      {/* Come funziona */}
      <section className="py-16 md:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#f59e0b]">
            Come funziona
          </div>
          <h2 className="mt-3 text-3xl md:text-4xl font-extrabold text-[#0a1f55]">
            4 step per attivare la tua opportunità extra.
          </h2>
          <div className="mt-10 grid md:grid-cols-4 gap-5">
            {[
              { n: "01", t: "Iscriviti", d: "Diventa socio ACDT e attiva la tessera ufficiale." },
              { n: "02", t: "Scegli il percorso", d: "Privato, professionista o azienda: definisci il tuo obiettivo." },
              { n: "03", t: "Formati e attiva", d: "Segui il piano formativo e attiva gli strumenti operativi." },
              { n: "04", t: "Genera valore", d: "Ricevi progetti dai partner e monetizza le competenze." },
            ].map((s) => (
              <div
                key={s.n}
                data-testid={`step-${s.n}`}
                className="p-6 rounded-2xl bg-white border border-slate-200 hover:border-[#f59e0b]/40 hover:shadow-md transition-all"
              >
                <div className="text-4xl font-extrabold text-[#f59e0b]">{s.n}</div>
                <div className="mt-2 text-lg font-extrabold text-[#0a1f55]">{s.t}</div>
                <div className="mt-1 text-slate-600 text-sm leading-relaxed">{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA finale */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-[#0a1f55] via-[#102a6b] to-[#1e3a8a] p-10 md:p-16">
            <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-[#f59e0b]/20 blur-3xl" />
            <div className="absolute -bottom-24 -right-24 h-80 w-80 rounded-full bg-white/10 blur-3xl" />
            <div className="relative grid lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="text-xs font-bold tracking-[0.22em] uppercase text-[#fbbf24]">
                  Inizia oggi
                </div>
                <h2 className="mt-3 text-3xl md:text-5xl font-extrabold text-white leading-[1.05]">
                  Attiva la tua opportunità extra con ACDT.
                </h2>
                <p className="mt-5 text-slate-200/90 text-lg max-w-2xl leading-relaxed">
                  Un solo passo: iscriviti all'associazione e accedi immediatamente a tutti
                  i percorsi, gli strumenti e i partner ACDT.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col gap-3">
                <Link to="/area-soci">
                  <Button
                    data-testid="opportunita-cta-finale-socio"
                    className="w-full bg-[#1e40af] hover:bg-[#1e3a8a] text-white font-bold h-12"
                  >
                    <LogIn className="mr-2 h-4 w-4" />
                    Accesso Soci
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to="/corso">
                  <Button
                    data-testid="opportunita-cta-finale-corso"
                    variant="outline"
                    className="w-full h-12 bg-white/5 border-white/20 text-white hover:bg-white/10 hover:text-white"
                  >
                    Scopri il corso
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <CandidaturaPartnerModal
        open={candidaturaOpen}
        onClose={() => setCandidaturaOpen(false)}
      />
    </div>
  );
}
